import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { useReportDetailQuery } from "app/store/transmission/api/transmissionDetailApi";

import { useTheme } from "@mui/material";
import { Box, Dialog, Button, Tooltip } from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";

import "components/pages/transmissions/modal/detail/ReportDetailModal.css";
import { getFullName } from "utils/string";
import {
  selectedReport,
  toggleModal,
  setExplicitReportNoteEditable,
  setCurrentPage,
  setPdfPages,
  resetClinicNotes,
  setPreviousReport,
  setReportNoteEditable,
  setPdfFileName,
  setReportAccountDetail,
  setReportNoteEditAccess,
  toggleNurseFlag,
  setFileToAttachToReport,
  setOriginalFileToAttachToReport,
  setFileToAttachSelected,
  setUpdatedTransmissionReportFile,
  setReportCurrentTab,
  setSelectedTab,
  toggleDrawer,
  resetEHRNotes,
  loadEHRNoteList,
  setMaximumAlertPriority,
  setAlertNotificationConfigs,
  setTransmissionReportPdf,
  setTransmissionReportPdfUrl,
  setCoverPageLength,
  setIsFetchingCoverPage,
  setShowSuggestionModal,
  setInvalidBillingDateResponse,
  setCrossFacilityReport,
  changeExpansion,
  resetProviderNotes,
  changeNextSendDate,
  changeBillableDate,
  changeOriginalBillableDate,
} from "app/store/transmission/slice/transmissionSlice";

import AlertBanner from "components/pages/transmissions/modal/detail/alert/AlertBanner";
import ReportHeaderButton from "components/pages/transmissions/modal/detail/headers/ReportHeaderButton";
import PdfContainer from "components/pages/transmissions/modal/detail/pdf/PdfContainer";
import usePdfViewer from "./pdf/viewer/usePdfViewer";
import { showPreferenceList } from "app/store/accountPreference/slice/accountPreferenceSlice";
import { getFormattedDate } from "utils/date";
import { getReportCurrentTab } from "utils/common";
import { setIsArchiveReport } from "app/store/archiveReport/slice/archiveReportSlice";
import { CLINIC_ROLES } from "static/constants";
import { checkUserAuditAccess } from "utils/report";
import useReportUserPresence from "./headers/useReportUserPresence";
import useCloseModalPage from "hooks/useCloseModalPage";
import useCtrlSpaceShortcut from "hooks/useCtrlSpaceShortcut";
import Loading from "components/common/loading/Loading";
import { NoteSaveHandlersProvider } from "./notes/NoteSaveHandlersContext";
import useUnsavedChanges from "hooks/useUnsavedChanges";
import useUnsavedNotes from "./unsaved_notes/useUnsavedNotes";
import UnsavedChangesModal from "components/common/modals/unsavedChangesModal/UnsavedChangesModal";

const ReportDetail = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const closeReportPage = useCloseModalPage();
  const { reportId: reportIdFromUrl } = useParams();

  const { reportId, isReportDetailModal, reportDetail, currentPageNumber } =
    useSelector((state) => state.transmission);
  const { accounts } = useSelector((state) => state.account);
  const { currentUser } = useSelector((state) => state.auth);

  const { activeUsers } = useReportUserPresence({ reportId });

  // Ctrl+Space signs and processes in some footers; elsewhere it must not act as Space on the focused control
  useCtrlSpaceShortcut(null, !!reportId && isReportDetailModal);

  const {
    numPages,
    isRegularPdfLoading,
    handlePDFPageNavigation,
    openRegularReportOnNewTab,
  } = usePdfViewer({
    reportId,
  });

  const { isFetching, data, isSuccess } = useReportDetailQuery(
    { reportId, isReportDetailModal },
    { refetchOnMountOrArgChange: true }
  );

  const [loadedReportId, setLoadedReportId] = useState("");

  useEffect(() => {
    if (!reportId) {
      setLoadedReportId("");
    } else if (isSuccess && !isFetching) {
      setLoadedReportId(reportId);
    }
  }, [reportId, isSuccess, isFetching]);

  const onClose = ({ fromHistory = false } = {}) => {
    dispatch(selectedReport(""));
    dispatch(setReportNoteEditable(false));
    dispatch(setReportNoteEditAccess(false));
    dispatch(setExplicitReportNoteEditable(false));
    dispatch(toggleModal({ type: "reportDetail", value: false }));
    dispatch(toggleModal({ type: "alertNotification", value: false }));
    dispatch(setMaximumAlertPriority(null));
    dispatch(setAlertNotificationConfigs(null));
    dispatch(setCurrentPage(0));
    dispatch(setPdfPages(0));
    dispatch(resetClinicNotes());
    dispatch(resetProviderNotes());
    dispatch(setPreviousReport(""));
    dispatch(setPdfFileName(null));
    dispatch(setReportAccountDetail(null));
    dispatch(setOriginalFileToAttachToReport(null));
    dispatch(setFileToAttachToReport(null));
    dispatch(setFileToAttachSelected(false));
    dispatch(setUpdatedTransmissionReportFile(null));
    dispatch(setOriginalFileToAttachToReport(null));
    dispatch(setReportCurrentTab(""));
    dispatch(setIsArchiveReport(false));
    dispatch(resetEHRNotes());
    dispatch(setTransmissionReportPdf(null));
    dispatch(setTransmissionReportPdfUrl(null));
    dispatch(setCoverPageLength(0));
    dispatch(setIsFetchingCoverPage(false));
    dispatch(setShowSuggestionModal({ displayPopup: false }));
    dispatch(setInvalidBillingDateResponse(null));
    dispatch(setCrossFacilityReport(false));
    dispatch(changeNextSendDate(null));
    dispatch(changeBillableDate(null));
    dispatch(changeOriginalBillableDate(null));

    if (reportIdFromUrl && !fromHistory) {
      closeReportPage({ report: reportDetail });
    }
  };

  const isOpen = !!reportId && isReportDetailModal;

  const { hasUnsavedNotes, getUnsavedNotes } = useUnsavedNotes();
  const { unsavedChanges, requestClose, discardAndLeave, keepEditing } =
    useUnsavedChanges({
      isOpen,
      onClose,
      hasUnsavedChanges: hasUnsavedNotes,
      getUnsavedChanges: getUnsavedNotes,
      closeOnHistoryPop: true,
    });

  useEffect(() => {
    if (isSuccess && data?._id === reportDetail?._id) {
      dispatch(
        selectedReport({
          id: data?._id,
          isLegacyReport: data?.legacy ?? true,
          reportDetail: data,
        })
      );
      dispatch(toggleNurseFlag(data?.nurse_flag));
      dispatch(
        showPreferenceList({
          accounts,
          selectedAccount: reportDetail?.account,
        })
      );
      if (data?.report_modification_history) {
        const reportModificationHistory = {
          pageInsertFrom: data.report_modification_history.page_insert_from,
          pageInsertTo: data.report_modification_history.page_insert_to || null,
          modifiedBy: data.report_modification_history.report_modified_by,
          modifiedAt: getFormattedDate(
            data.report_modification_history.report_modified_at,
            "M/D/YYYY h:mmA"
          ),
          ...data?.report_modification_history,
        };
        dispatch(setFileToAttachToReport(reportModificationHistory));
        dispatch(setOriginalFileToAttachToReport(reportModificationHistory));
      }

      if (data?.ehr_notes) {
        dispatch(loadEHRNoteList(data?.ehr_notes || []));
      }

      const currentAccount = accounts?.find(
        (account) =>
          account?.name === data?.account ||
          account?.facility?.includes(data?.account)
      );
      dispatch(setReportAccountDetail(currentAccount));

      const reportCurrentTab = getReportCurrentTab(data);
      dispatch(setReportCurrentTab(reportCurrentTab));

      // A report opened by link behaves as if opened from its own tab
      if (reportIdFromUrl && reportCurrentTab) {
        dispatch(setSelectedTab(reportCurrentTab));
      }

      const isCrossFacilityReport =
        data?.cross_facility_report && CLINIC_ROLES.includes(currentUser?.role);
      dispatch(setCrossFacilityReport(isCrossFacilityReport));
      const hasAuditAccess = checkUserAuditAccess(currentUser, data);

      if (hasAuditAccess) {
        dispatch(changeExpansion({ type: "tasks", value: true }));
      }
    }
  }, [
    accounts,
    isSuccess,
    data,
    dispatch,
    reportDetail?._id,
    reportDetail?.account,
    currentUser,
    reportIdFromUrl,
  ]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowUp") {
        handlePDFPageNavigation(currentPageNumber - 1);
      } else if (e.key === "ArrowDown") {
        handlePDFPageNavigation(currentPageNumber + 1);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentPageNumber, handlePDFPageNavigation, numPages]);

  useEffect(() => {
    const localStorageKey = `isDrawerOpen:${currentUser?.userId}`;
    const drawerValue = localStorage.getItem(localStorageKey);

    const isDrawerOpen = drawerValue === "false" ? false : true;
    dispatch(toggleDrawer(isDrawerOpen));
  }, [currentUser?.userId, dispatch]);

  return (
    <Dialog
      fullScreen
      open={isOpen}
      onClose={() => requestClose()}
      className="view-pdf--full-dialog"
    >
      {loadedReportId !== reportId || data?._id !== reportDetail?._id ? (
        <Loading height="100%" width="100%" />
      ) : (
        <>
          <Box
            className="dialog-title"
            onClick={() => requestClose()}
            style={{
              cursor: "pointer",
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <div className="dialog-title-content">
              <Tooltip title="Back" placement="bottom">
                <Button variant="text" style={{ color: "#638215" }}>
                  <ArrowBackIcon />
                </Button>
              </Tooltip>
              <span className="report-title">
                {`Transmissions Report - ${getFullName(data?.name ?? "")}`}
              </span>
            </div>

            <ReportHeaderButton data={reportDetail} activeUsers={activeUsers} />
          </Box>
          <Box className="dialog-content">
            <div className="dialog-content__body">
              <div className="dialog-content__alert">
                <AlertBanner serialNumber={data?.patient_serial_num} />
              </div>

              <Box className="dialog-content__report">
                <PdfContainer
                  data={data}
                  isFetching={isFetching}
                  isRegularPdfLoading={isRegularPdfLoading}
                  openRegularReportOnNewTab={openRegularReportOnNewTab}
                />
              </Box>
            </div>
          </Box>
        </>
      )}

      {unsavedChanges && (
        <UnsavedChangesModal
          changes={unsavedChanges}
          onCancel={keepEditing}
          onConfirm={discardAndLeave}
        />
      )}
    </Dialog>
  );
};

const ReportDetailModal = () => (
  <NoteSaveHandlersProvider>
    <ReportDetail />
  </NoteSaveHandlersProvider>
);

export default ReportDetailModal;
